import { useThemeColors } from '@codezo/theme';
import { AppShell } from '@mantine/core';
import { createContext, useContext, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';

interface SectionContextType {
	currentSection: number;
	setCurrentSection: (_section: number) => void;
}

const SectionContext = createContext<SectionContextType>({
	currentSection: 0,
	setCurrentSection: () => {},
});

export const useSectionContext = () => useContext(SectionContext);

export function PublicLayout() {
	const colors = useThemeColors();
	const location = useLocation();
	const [currentSection, setCurrentSection] = useState(0);

	const isLandingPage = location.pathname === '/';

	// Define header colors based on section
	const getHeaderBackground = () => {
		if (!isLandingPage) return colors.background;

		switch (currentSection) {
			case 0: // Hero section - transparent to blend with hero
				return 'transparent';
			case 1: // How it works section - light background
				return 'rgba(250, 250, 250, 0.95)';
			case 2: // 05 + Partners section - dark background
				return '#3C3C3C';
			case 3: // CTA section - dark background
				return '#2A2A2A';
			case 4: // Footer section - dark background
				return '#1A1A1A';
			default:
				return 'transparent'; // Default to transparent
		}
	};

	return (
		<SectionContext.Provider value={{ currentSection, setCurrentSection }}>
			<AppShell
				header={{ height: 72 }}
				styles={{
					main: {
						backgroundColor: colors.background,
						minHeight: '100vh',
					},
				}}
			>
				<AppShell.Header
					style={{
						backgroundColor: getHeaderBackground(),
						backdropFilter: 'blur(8px)',
						borderBottom: isLandingPage ? 'none' : '1px solid #E5E7EB',
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						zIndex: 1000,
						transition: 'background-color 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
					}}
				>
					<PublicHeader currentSection={isLandingPage ? currentSection : 0} />
				</AppShell.Header>

				<AppShell.Main>
					<Outlet />
				</AppShell.Main>
			</AppShell>
		</SectionContext.Provider>
	);
}
